import { useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useResource, formatDate, titleCase } from "../../lib/hooks";
import { useAuth } from "../../context/AuthContext";
import { EntityForm, Field } from "../../components/EntityForm";
import { FormWizard } from "../../components/FormWizard";
import { feedbackRegisterFields } from "../../lib/feedbackIntake";
import type { Participant } from "../../lib/types";

/**
 * The quality registers: incident reports (TC-INR), investigations (TC-INI)
 * and feedback / complaints / compliments (TC-FBK / TC-CML / TC-CMP).
 *
 * Each organisation sees only its own records — that is enforced by row-level
 * security in the database (migration 078), not by this page, so a partner
 * cannot reach another provider's incidents even through a crafted request.
 *
 * These are the registers and the core intake. The source forms carry about
 * 235 fields between them; the columns here are what a register lists and
 * reports on, and the remaining detail is stored alongside each record, so the
 * full page-by-page forms can land next without another table change.
 */

type Tab = "cases" | "incidents" | "investigations" | "feedback" | "extended" | "commission";

/** Q6: the eight further registers, one family with a discriminator. */
const EXTENDED_REGISTERS = [
    ["risk", "Risk"],
    ["corrective_action", "Corrective actions"],
    ["preventive_action", "Preventive actions"],
    ["audit", "Audits"],
    ["audit_finding", "Audit findings"],
    ["continuous_improvement", "Continuous improvement"],
    ["quality_review", "Quality reviews"],
    ["governance", "Governance"],
] as const;

const registerLabel = (key: string): string =>
    EXTENDED_REGISTERS.find(([k]) => k === key)?.[1] ?? titleCase(key.replace(/_/g, " "));

/** The short register entry, or the full form page by page. */
type Dialog = { tab: Tab; full: boolean } | null;

interface IncidentReportRow {
    id: string;
    ref_code: string | null;
    company_name: string | null;
    reporter_name: string | null;
    affected_name: string | null;
    occurred_at: string | null;
    incident_type: string | null;
    initial_severity: string | null;
    is_reportable: boolean;
    summary: string | null;
    status: string;
    submitted_at: string;
    investigation_ref: string | null;
}

interface InvestigationRow {
    id: string;
    ref_code: string | null;
    company_name: string | null;
    incident_report_reference: string | null;
    report_ref: string | null;
    investigator_name: string | null;
    assigned_to: string | null;
    assignee_name: string | null;
    target_completion_date: string | null;
    severity_override: string | null;
    root_cause_summary: string | null;
    status: string;
    created_at: string;
}

/** Q2: someone who currently resolves the investigate permission. */
interface AssigneeOption {
    id: string;
    fullName: string;
    role: string;
}

/** Q5: the parent record that owns a register entry and its investigation. */
interface QualityCaseRow {
    id: string;
    ref_code: string | null;
    company_name: string | null;
    case_type: string;
    title: string | null;
    status: string;
    opened_at: string;
    approved_at: string | null;
    approval_note: string | null;
    closed_at: string | null;
    closure_note: string | null;
    report_ref: string | null;
    feedback_ref: string | null;
    investigation_ref: string | null;
    investigation_count: number;
}

const caseStatusChip = (s: string) =>
    s === "closed" ? "pass" : s === "approved" ? "pass"
        : s === "under_investigation" || s === "awaiting_approval" ? "info" : "warn";

/** The Commission deadline wall (migrations 085 + 093). */
interface CommissionClockRow {
    id: string;
    incident_report_id: string;
    category: string;
    became_aware_at: string;
    harm_caused: boolean;
    notify_due_at: string | null;
    detail_due_at: string;
    notified_at: string | null;
    detail_submitted_at: string | null;
    final_report_requested_at: string | null;
    final_report_due_at: string | null;
    final_report_submitted_at: string | null;
    commission_reference: string | null;
    ref_code: string;
    summary: string | null;
    company_name: string | null;
}

interface UnstartedReportable {
    id: string;
    ref_code: string;
    summary: string | null;
    submitted_at: string;
    company_name: string | null;
}

interface CommissionWall {
    clocks: CommissionClockRow[];
    unstarted: UnstartedReportable[];
    categories: Array<{ key: string; label: string }>;
}

/**
 * The countdown chip: done is quiet, overdue shouts, inside six hours warns.
 * The wording carries the time left so nobody has to do deadline arithmetic
 * during an incident.
 */
const deadlineChip = (dueAt: string | null, doneAt: string | null): { text: string; tone: string } => {
    if (!dueAt) return { text: "n/a", tone: "mute" };
    if (doneAt) return { text: `Lodged ${formatDate(doneAt)}`, tone: "pass" };
    const msLeft = new Date(dueAt).getTime() - Date.now();
    if (msLeft <= 0) return { text: "OVERDUE", tone: "block" };
    const hours = Math.floor(msLeft / 3_600_000);
    if (hours < 6) return { text: `${Math.max(1, hours)}h left`, tone: "block" };
    if (hours < 24) return { text: `${hours}h left`, tone: "warn" };
    return { text: `${Math.floor(hours / 24)}d ${hours % 24}h left`, tone: "info" };
};

interface ExtendedEntryRow {
    id: string;
    ref_code: string | null;
    company_name: string | null;
    register: string;
    title: string;
    description: string | null;
    rating: string | null;
    status: string;
    due_date: string | null;
    owner_name: string | null;
    case_ref: string | null;
    closed_at: string | null;
    created_at: string;
}

interface FeedbackRow {
    id: string;
    ref_code: string | null;
    company_name: string | null;
    submission_type: string;
    submission_source: string;
    anonymous: boolean;
    submitter_name: string | null;
    affected_name: string | null;
    occurred_on: string | null;
    risk_rating: string | null;
    category: string | null;
    description: string | null;
    status: string;
    submitted_at: string;
    acknowledged_at: string | null;
    resolved_at: string | null;
}

const SEVERITY = ["low", "medium", "high", "critical"].map((s) => ({ value: s, label: titleCase(s) }));

const severityChip = (s: string | null) =>
    s === "critical" ? "block" : s === "high" ? "warn" : s === "medium" ? "info" : "mute";

const statusChip = (s: string) =>
    s === "closed" || s === "resolved" ? "pass"
        : s === "new" || s === "submitted" || s === "open" ? "warn" : "info";

/** Feedback, complaint and compliment each carry their own reference prefix. */
const typeChip = (t: string) => (t === "complaint" ? "warn" : t === "compliment" ? "pass" : "info");

interface LookupResult {
    ref_code: string;
    affected_name: string | null;
    participant_name: string | null;
    occurred_at: string | null;
    initial_severity: string | null;
    incident_type: string | null;
}

/**
 * When the investigator types a TC-INR reference, look the report up (under
 * their own row-level security) and carry its details into the form: the
 * participant's name and the severity as first reported. The form asked these
 * as prefills in Teams; here the register itself is the source. The register's
 * "critical" is the form's "extreme" - same thing, translated back.
 */
const lookupIncident = async (reference: string) => {
    const found = await api
        .get<LookupResult>(`/incident-investigations/lookup?reference=${encodeURIComponent(reference)}`)
        .catch(() => null);
    if (!found) return null;
    const who = found.participant_name ?? found.affected_name;
    return {
        label: [who, found.incident_type, found.occurred_at ? formatDate(found.occurred_at) : null]
            .filter(Boolean).join(", ") || "incident report",
        answers: {
            ...(who ? { snapshotParticipantName: who } : {}),
            ...(found.initial_severity
                ? { snapshotOriginalSeverity: found.initial_severity === "critical" ? "extreme" : found.initial_severity }
                : {}),
        },
    };
};

export const QualityRegisters = () => {
    const { user, mayQuality } = useAuth();
    // Q1: every button follows the caller's RESOLVED permissions - role
    // defaults plus any overrides - not their role name. A staff member may
    // file an incident without seeing the register; a delegated investigator
    // reads through their investigate grant; the API enforces all of it again.
    const mayView = ["quality.records.view", "quality.incident.investigate", "quality.complaint.investigate"]
        .some(mayQuality);
    const mayFile: Record<Tab, boolean> = {
        cases: false, // a case is minted by filing a record, never directly
        incidents: mayQuality("quality.incident.submit"),
        investigations: mayQuality("quality.incident.investigate"),
        feedback: ["quality.complaint.submit", "quality.feedback.submit", "quality.compliment.submit"]
            .some(mayQuality),
        extended: mayQuality("quality.registers.manage"),
        commission: false, // clocks start from the wall itself, per incident
    };
    const isStaff = ["admin", "manager", "supervisor"].includes(user?.role ?? "");

    const [tab, setTab] = useState<Tab>("incidents");
    const [dialog, setDialog] = useState<Dialog>(null);

    const incidents = useResource<IncidentReportRow[]>(mayView ? "/incident-reports" : null);
    const investigations = useResource<InvestigationRow[]>(mayView ? "/incident-investigations" : null);
    const feedback = useResource<FeedbackRow[]>(mayView ? "/feedback-submissions" : null);
    const participants = useResource<Participant[]>("/participants");
    // Q2: who an investigation can be handed to - answered by the permission
    // engine (role default or active grant), so a delegated staff member
    // appears here for exactly as long as their grant lasts.
    const mayAssign = mayQuality("quality.incident.investigate");
    const assignees = useResource<AssigneeOption[]>(mayAssign ? "/incident-investigations/assignees" : null);
    const [assigning, setAssigning] = useState<string | null>(null);

    const handOver = async (investigationId: string, assignedTo: string | null) => {
        setAssigning(investigationId);
        try {
            await api.post(`/incident-investigations/${investigationId}/assign`, { assignedTo });
            investigations.reload();
        } finally {
            setAssigning(null);
        }
    };

    // Q5: the case workflow. Approve and close are the two Q1 permissions
    // that finally bite here; the API refuses an unapproved close anyway.
    const mayApprove = mayQuality("quality.investigation.approve");
    const mayClose = mayQuality("quality.records.close");
    const cases = useResource<QualityCaseRow[]>(mayView ? "/quality-cases" : null);
    const [caseError, setCaseError] = useState<string | null>(null);

    // Q6: the eight further registers. Recording and closing entries are
    // their own engine permissions, delegable like everything since Q1.
    const mayCloseEntry = mayQuality("quality.registers.close");
    const [extRegister, setExtRegister] = useState<string>("");
    const entries = useResource<ExtendedEntryRow[]>(
        mayView ? `/quality-register-entries${extRegister ? `?register=${extRegister}` : ""}` : null);

    const closeEntry = async (entryId: string) => {
        await api.post(`/quality-register-entries/${entryId}/close`, {});
        entries.reload();
    };

    // The Commission clock: starting one and recording lodgements belong to
    // whoever may investigate incidents; the wall is visible to readers.
    const wall = useResource<CommissionWall>(mayView ? "/commission-clocks" : null);
    const [startCategory, setStartCategory] = useState<Record<string, string>>({});
    const [startHarm, setStartHarm] = useState<Record<string, boolean>>({});

    const startClock = async (incidentId: string) => {
        const category = startCategory[incidentId];
        if (!category) return;
        await api.post(`/incident-reports/${incidentId}/commission-clock`, {
            category,
            // 093: harm moves restrictive practice onto the 24-hour path.
            harmCaused: category === "restrictive_practice" ? (startHarm[incidentId] ?? false) : false,
        });
        wall.reload();
        incidents.reload();
    };

    const recordLodgement = async (
        clockId: string, step: "notified" | "detail-submitted" | "final-report-submitted"
    ) => {
        await api.post(`/commission-clocks/${clockId}/${step}`, {});
        wall.reload();
    };

    /** s 24 (093): the Commissioner has required a final report - start the
     *  60-business-day clock from now. */
    const requestFinalReport = async (clockId: string) => {
        await api.post(`/commission-clocks/${clockId}/final-report-requested`, {});
        wall.reload();
    };

    const actOnCase = async (caseId: string, action: "approve" | "close") => {
        setCaseError(null);
        try {
            await api.post(`/quality-cases/${caseId}/${action}`, {});
            cases.reload();
            incidents.reload();
            feedback.reload();
        } catch (err) {
            setCaseError(err instanceof Error ? err.message : "The case could not be updated");
        }
    };

    const participantOptions = useMemo(
        () => (participants.data ?? []).map((p) => ({ value: p.id, label: p.full_name })),
        [participants.data]
    );

    const refresh = () => {
        setDialog(null);
        incidents.reload();
        investigations.reload();
        feedback.reload();
        cases.reload();
        entries.reload();
    };

    const incidentFields: Field[] = [
        { name: "summary", label: "What happened?", kind: "textarea", required: true,
          help: "A factual account. For the full incident form, page by page, choose “Report an incident” instead." },
        { name: "occurredAt", label: "When did it happen?", kind: "datetime", half: true },
        { name: "incidentType", label: "Type of incident", kind: "text", half: true,
          suggestions: ["Fall", "Medication", "Injury", "Behaviour", "Property damage",
                        "Privacy or security", "Environmental", "Near miss", "Skin integrity"] },
        { name: "initialSeverity", label: "Initial severity", kind: "select", options: SEVERITY, half: true },
        { name: "participantId", label: "Participant", kind: "searchselect", options: participantOptions, half: true },
        { name: "affectedName", label: "Who was affected", kind: "text", half: true },
        { name: "locationText", label: "Where it happened", kind: "text", half: true },
        { name: "reporterName", label: "Reported by", kind: "text", half: true },
        { name: "reporterEmail", label: "Reporter email", kind: "email", half: true },
        { name: "harmOccurred", label: "Harm occurred", kind: "checkbox", half: true },
        { name: "isReportable", label: "Possibly NDIS reportable", kind: "checkbox", half: true,
          help: "Tick if it may meet a reportable incident category. Reportable incidents carry statutory notification timeframes." },
    ];

    const investigationFields: Field[] = [
        { name: "incidentReportReference", label: "Incident report reference", kind: "text", required: true,
          placeholder: "TC-INR-2026-00042",
          help: "The CRM looks this up and links the investigation to that report. You can only resolve a reference belonging to your own organisation." },
        { name: "manualEntry", label: "Reference cannot be found — record details by hand", kind: "checkbox",
          help: "Leave unticked so a mistyped reference is caught rather than filed against nothing." },
        { name: "investigatorName", label: "Assigned investigator", kind: "text", half: true },
        { name: "investigatorEmail", label: "Investigator email", kind: "email", half: true },
        { name: "assignedAt", label: "Date assigned", kind: "datetime", half: true },
        { name: "targetCompletionDate", label: "Target completion", kind: "date", half: true },
        { name: "severityOverride", label: "Severity on review", kind: "select", options: SEVERITY, half: true,
          help: "Only if the investigation disagrees with the severity first recorded." },
        { name: "overrideRationale", label: "Reason for the change", kind: "textarea" },
        { name: "rootCauseSummary", label: "Root cause summary", kind: "textarea",
          help: "For the full RCA, CAPA plan and sign-off pages, choose “Open an investigation” instead." },
    ];

    // Shared with the Website inbox's "record in register" promote, so a
    // submission filed from either place produces the same record.
    const feedbackFields: Field[] = feedbackRegisterFields({ participants: participants.data ?? [] });

    const tabs: Array<{ key: Tab; label: string; count: number }> = [
        // Cases lead when the viewer can see them - the case is the thread
        // the registers hang off. Hidden for submit-only users like the rest.
        ...(mayView ? [{ key: "cases" as Tab, label: "Quality cases", count: cases.data?.length ?? 0 }] : []),
        { key: "incidents", label: "Incident reports", count: incidents.data?.length ?? 0 },
        { key: "investigations", label: "Investigations", count: investigations.data?.length ?? 0 },
        { key: "feedback", label: "Feedback & complaints", count: feedback.data?.length ?? 0 },
        ...(mayView ? [{ key: "extended" as Tab, label: "Extended registers", count: entries.data?.length ?? 0 }] : []),
        ...(mayView ? [{
            key: "commission" as Tab, label: "Commission clock",
            count: (wall.data?.clocks.filter((c) => !c.detail_submitted_at).length ?? 0)
                + (wall.data?.unstarted.length ?? 0),
        }] : []),
    ];

    const newLabel: Record<Tab, string> = {
        cases: "",
        incidents: "+ Report an incident",
        investigations: "+ Open an investigation",
        feedback: "+ Record feedback",
        extended: "+ Record an entry",
        commission: "",
    };

    const extendedFields: Field[] = [
        { name: "register", label: "Register", kind: "select", required: true,
          options: EXTENDED_REGISTERS.map(([value, label]) => ({ value, label })) },
        { name: "title", label: "Title", kind: "text", required: true },
        { name: "description", label: "Description", kind: "textarea" },
        { name: "rating", label: "Rating", kind: "select", options: SEVERITY, half: true },
        { name: "dueDate", label: "Due date", kind: "date", half: true },
        { name: "ownerName", label: "Owner", kind: "text", half: true },
    ];

    return (
        <div style={{ display: "grid", gap: "0.85rem" }}>
            {dialog?.tab === "incidents" && dialog.full && (
                <FormWizard
                    formName="incident-report"
                    title="Incident report"
                    subtitle="TrustCare's own incident form, page by page. Filed against your organisation and given a TC-INR reference."
                    path="/incident-reports"
                    allowDraft
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.tab === "incidents" && !dialog.full && (
                <EntityForm title="Short register entry" path="/incident-reports" fields={incidentFields}
                    subtitle="For an incident phoned in or already recorded elsewhere. It is a register entry, not an incomplete form."
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.tab === "investigations" && dialog.full && (
                <FormWizard
                    formName="incident-investigation"
                    title="Incident investigation"
                    subtitle="TrustCare's own investigation form. Name the incident report it is about and the CRM links the two, so the investigation carries its details rather than you retyping them."
                    path="/incident-investigations"
                    allowDraft
                    linker={{ field: "incidentReportReferenceNumber", fetch: lookupIncident }}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.tab === "investigations" && !dialog.full && (
                <EntityForm title="Short register entry" path="/incident-investigations" fields={investigationFields}
                    subtitle="For an investigation being tracked elsewhere. It is a register entry, not an incomplete form."
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.tab === "feedback" && dialog.full && (
                <FormWizard
                    formName="feedback-complaint"
                    title="Feedback, complaint or compliment"
                    subtitle="TrustCare's own feedback and complaints form. The reference follows what it is: TC-CML for a complaint, TC-CMP for a compliment, TC-FBK for feedback."
                    path="/feedback-submissions"
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.tab === "feedback" && !dialog.full && (
                <EntityForm title="Short register entry" path="/feedback-submissions"
                    fields={feedbackFields}
                    subtitle="For something raised in person or by phone. It is a register entry, not an incomplete form."
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.tab === "extended" && (
                <EntityForm title="Record a register entry" path="/quality-register-entries"
                    fields={extendedFields}
                    subtitle="Risk, corrective and preventive actions, audits and findings, continuous improvement, quality reviews and governance — each takes its own house reference."
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}

            <div className="card">
                <div className="card-head">
                    <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
                        {tabs.map((t) => (
                            <button key={t.key}
                                className={tab === t.key ? "btn" : "icon-btn"}
                                onClick={() => setTab(t.key)}>
                                {t.label} <span className="chip mute" style={{ marginLeft: 4 }}>{t.count}</span>
                            </button>
                        ))}
                    </div>
                    {mayFile[tab] && (
                        <div style={{ display: "flex", gap: "0.35rem" }}>
                            {tab !== "extended" && (
                                <button className="icon-btn" onClick={() => setDialog({ tab, full: false })}
                                    title="For something phoned in, raised in person, or already recorded elsewhere.">
                                    Short entry
                                </button>
                            )}
                            <button className="btn" onClick={() => setDialog({ tab, full: tab !== "extended" })}>
                                {newLabel[tab]}
                            </button>
                        </div>
                    )}
                </div>

                <div className="card-body flush">
                    {!mayView && (
                        <p className="empty" style={{ padding: "1.1rem" }}>
                            You can file reports from the buttons above — reporting is never blocked.
                            Seeing the registers themselves needs the &ldquo;View the quality
                            registers&rdquo; permission, which an admin can grant.
                        </p>
                    )}
                    {mayView && caseError && tab === "cases" && (
                        <p className="empty" style={{ padding: "0.6rem 1.1rem", color: "var(--danger, #b3261e)" }}>
                            {caseError}
                        </p>
                    )}
                    {mayView && <div className="table-wrap">
                        {tab === "cases" && (
                            <table>
                                <thead><tr>
                                    <th>Case</th><th>Type</th><th>About</th><th>Owns</th>
                                    <th>Status</th><th>Actions</th>{isStaff && <th>Organisation</th>}
                                </tr></thead>
                                <tbody>
                                    {(cases.data ?? []).map((qc) => (
                                        <tr key={qc.id}>
                                            <td className="mono" style={{ fontWeight: 600 }}>{qc.ref_code ?? "—"}</td>
                                            <td><span className={`chip ${typeChip(qc.case_type)}`}>{titleCase(qc.case_type)}</span></td>
                                            <td style={{ maxWidth: 300 }}>
                                                <div style={{ overflow: "hidden", display: "-webkit-box",
                                                    WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                                                    {qc.title ?? "—"}
                                                </div>
                                            </td>
                                            <td className="mono" style={{ fontSize: "0.85em" }}>
                                                {[qc.report_ref, qc.feedback_ref, qc.investigation_ref]
                                                    .filter(Boolean).join(" · ") || "—"}
                                            </td>
                                            <td>
                                                <span className={`chip ${caseStatusChip(qc.status)}`}>
                                                    {titleCase(qc.status.replace(/_/g, " "))}
                                                </span>
                                                {qc.closed_at && (
                                                    <div className="mono" style={{ marginTop: 3, fontSize: "0.82em" }}>
                                                        Closed {formatDate(qc.closed_at)}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                {qc.status !== "closed" && (
                                                    <div style={{ display: "flex", gap: "0.3rem" }}>
                                                        {mayApprove && qc.status !== "approved" && qc.investigation_count > 0 && (
                                                            <button className="icon-btn" onClick={() => actOnCase(qc.id, "approve")}
                                                                title="Sign off what the investigation found.">
                                                                Approve
                                                            </button>
                                                        )}
                                                        {mayClose && (
                                                            <button className="icon-btn" onClick={() => actOnCase(qc.id, "close")}
                                                                title="An investigated case must be approved before it can close.">
                                                                Close
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            {isStaff && <td>{qc.company_name ?? "TrustCare"}</td>}
                                        </tr>
                                    ))}
                                    {(cases.data ?? []).length === 0 && (
                                        <tr><td colSpan={isStaff ? 7 : 6} className="empty">
                                            No cases yet. Filing an incident, complaint, feedback or compliment opens one automatically.
                                        </td></tr>
                                    )}
                                </tbody>
                            </table>
                        )}

                        {tab === "incidents" && (
                            <table>
                                <thead><tr>
                                    <th>Reference</th><th>Occurred</th><th>Type</th><th>Affected</th>
                                    <th>Severity</th><th>Status</th>{isStaff && <th>Organisation</th>}
                                </tr></thead>
                                <tbody>
                                    {(incidents.data ?? []).map((r) => (
                                        <tr key={r.id}>
                                            <td>
                                                <div className="mono" style={{ fontWeight: 600 }}>{r.ref_code ?? "—"}</div>
                                                {r.is_reportable && <span className="chip block" style={{ fontWeight: 700 }}>Reportable</span>}
                                                {r.investigation_ref && (
                                                    <div className="chip pass mono" style={{ marginTop: 3, fontWeight: 700 }}>{r.investigation_ref}</div>
                                                )}
                                            </td>
                                            <td className="mono">{r.occurred_at ? formatDate(r.occurred_at) : "—"}</td>
                                            <td>{r.incident_type ?? "—"}</td>
                                            <td>{r.affected_name ?? "—"}</td>
                                            <td><span className={`chip ${severityChip(r.initial_severity)}`}>
                                                {r.initial_severity ? titleCase(r.initial_severity) : "—"}</span></td>
                                            <td><span className={`chip ${statusChip(r.status)}`}>{titleCase(r.status)}</span></td>
                                            {isStaff && <td>{r.company_name ?? "TrustCare"}</td>}
                                        </tr>
                                    ))}
                                    {(incidents.data ?? []).length === 0 && (
                                        <tr><td colSpan={isStaff ? 7 : 6} className="empty">
                                            No incident reports yet. Reporting one gives it a TC-INR reference and an entry in this register.
                                        </td></tr>
                                    )}
                                </tbody>
                            </table>
                        )}

                        {tab === "investigations" && (
                            <table>
                                <thead><tr>
                                    <th>Reference</th><th>Investigating</th><th>Investigator</th>
                                    <th>Held by</th>
                                    <th>Target</th><th>Status</th>{isStaff && <th>Organisation</th>}
                                </tr></thead>
                                <tbody>
                                    {(investigations.data ?? []).map((v) => (
                                        <tr key={v.id}>
                                            <td className="mono" style={{ fontWeight: 600 }}>{v.ref_code ?? "—"}</td>
                                            <td>
                                                <span className="mono">{v.report_ref ?? v.incident_report_reference ?? "—"}</span>
                                                {!v.report_ref && (
                                                    <span className="chip warn" style={{ marginLeft: 5 }}>entered by hand</span>
                                                )}
                                            </td>
                                            <td>{v.investigator_name ?? "—"}</td>
                                            <td>
                                                {mayAssign ? (
                                                    <select
                                                        aria-label={`Hand investigation ${v.ref_code ?? v.id} to`}
                                                        value={v.assigned_to ?? ""}
                                                        disabled={assigning === v.id}
                                                        onChange={(e) => handOver(v.id, e.target.value || null)}>
                                                        <option value="">— unassigned —</option>
                                                        {(assignees.data ?? []).map((a) => (
                                                            <option key={a.id} value={a.id}>
                                                                {a.fullName} ({titleCase(a.role)})
                                                            </option>
                                                        ))}
                                                        {/* An assignee whose grant has since lapsed still shows by name. */}
                                                        {v.assigned_to && !(assignees.data ?? []).some((a) => a.id === v.assigned_to) && (
                                                            <option value={v.assigned_to}>{v.assignee_name ?? "Assigned"}</option>
                                                        )}
                                                    </select>
                                                ) : (
                                                    v.assignee_name ?? "—"
                                                )}
                                            </td>
                                            <td className="mono">{v.target_completion_date ? formatDate(v.target_completion_date) : "—"}</td>
                                            <td>
                                                <span className={`chip ${statusChip(v.status)}`}>{titleCase(v.status)}</span>
                                                {/* Q4: the outcome is part of the record the submitter is owed. */}
                                                {v.root_cause_summary && (
                                                    <div style={{ maxWidth: 260, marginTop: 3, fontSize: "0.82em",
                                                        overflow: "hidden", display: "-webkit-box",
                                                        WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                                                        {v.root_cause_summary}
                                                    </div>
                                                )}
                                            </td>
                                            {isStaff && <td>{v.company_name ?? "TrustCare"}</td>}
                                        </tr>
                                    ))}
                                    {(investigations.data ?? []).length === 0 && (
                                        <tr><td colSpan={isStaff ? 7 : 6} className="empty">
                                            No investigations open. Opening one against a TC-INR reference links it to that report automatically.
                                        </td></tr>
                                    )}
                                </tbody>
                            </table>
                        )}

                        {tab === "commission" && (
                            <>
                                {(wall.data?.unstarted.length ?? 0) > 0 && (
                                    <table>
                                        <thead><tr>
                                            <th colSpan={isStaff ? 4 : 3} style={{ color: "var(--danger, #b3261e)" }}>
                                                Flagged reportable — no clock started. The statutory clock is running regardless.
                                            </th>
                                        </tr></thead>
                                        <tbody>
                                            {(wall.data?.unstarted ?? []).map((u) => (
                                                <tr key={u.id}>
                                                    <td className="mono" style={{ fontWeight: 600 }}>{u.ref_code}</td>
                                                    <td style={{ maxWidth: 320 }}>{u.summary ?? "—"}</td>
                                                    <td>
                                                        {mayAssign ? (
                                                            <div style={{ display: "flex", gap: "0.3rem", alignItems: "center", flexWrap: "wrap" }}>
                                                                <select aria-label={`Category for ${u.ref_code}`}
                                                                    value={startCategory[u.id] ?? ""}
                                                                    onChange={(e) => setStartCategory({ ...startCategory, [u.id]: e.target.value })}>
                                                                    <option value="">Choose a category…</option>
                                                                    {(wall.data?.categories ?? []).map((c) => (
                                                                        <option key={c.key} value={c.key}>{c.label}</option>
                                                                    ))}
                                                                </select>
                                                                {startCategory[u.id] === "restrictive_practice" && (
                                                                    <label style={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.85em" }}>
                                                                        <input type="checkbox" checked={startHarm[u.id] ?? false}
                                                                            onChange={(e) => setStartHarm({ ...startHarm, [u.id]: e.target.checked })} />
                                                                        Caused harm (24-hour path)
                                                                    </label>
                                                                )}
                                                                <button className="btn small" disabled={!startCategory[u.id]}
                                                                    onClick={() => void startClock(u.id)}>
                                                                    Start the clock
                                                                </button>
                                                            </div>
                                                        ) : <span className="chip warn">awaiting categorisation</span>}
                                                    </td>
                                                    {isStaff && <td>{u.company_name ?? "TrustCare"}</td>}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                                <table>
                                    <thead><tr>
                                        <th>Incident</th><th>Category</th><th>24-hour notification</th>
                                        <th>5-day detailed report</th><th>s 24 final report</th><th>Commission ref</th>
                                        {isStaff && <th>Organisation</th>}
                                    </tr></thead>
                                    <tbody>
                                        {(wall.data?.clocks ?? []).map((c) => {
                                            const notifyChip = deadlineChip(c.notify_due_at, c.notified_at);
                                            const detailChip = deadlineChip(c.detail_due_at, c.detail_submitted_at);
                                            const finalChip = deadlineChip(c.final_report_due_at, c.final_report_submitted_at);
                                            return (
                                                <tr key={c.id}>
                                                    <td>
                                                        <div className="mono" style={{ fontWeight: 600 }}>{c.ref_code}</div>
                                                        <div style={{ maxWidth: 260, fontSize: "0.85em", overflow: "hidden",
                                                            display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical" }}>
                                                            {c.summary ?? ""}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {titleCase(c.category.replace(/_/g, " "))}
                                                        {c.category === "restrictive_practice" && c.harm_caused && (
                                                            <span className="chip warn" style={{ marginLeft: 5 }}>harm</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className={`chip ${notifyChip.tone}`}>{notifyChip.text}</span>
                                                        {mayAssign && c.notify_due_at && !c.notified_at && (
                                                            <button className="icon-btn" style={{ marginLeft: 5 }}
                                                                onClick={() => void recordLodgement(c.id, "notified")}>
                                                                Mark lodged
                                                            </button>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className={`chip ${detailChip.tone}`}>{detailChip.text}</span>
                                                        {mayAssign && !c.detail_submitted_at && (
                                                            <button className="icon-btn" style={{ marginLeft: 5 }}
                                                                onClick={() => void recordLodgement(c.id, "detail-submitted")}>
                                                                Mark lodged
                                                            </button>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {c.final_report_requested_at ? (
                                                            <>
                                                                <span className={`chip ${finalChip.tone}`}>{finalChip.text}</span>
                                                                {mayAssign && !c.final_report_submitted_at && (
                                                                    <button className="icon-btn" style={{ marginLeft: 5 }}
                                                                        onClick={() => void recordLodgement(c.id, "final-report-submitted")}>
                                                                        Mark lodged
                                                                    </button>
                                                                )}
                                                            </>
                                                        ) : mayAssign ? (
                                                            <button className="icon-btn"
                                                                title="Records that the Commissioner has required a final report - 60 business days from now"
                                                                onClick={() => void requestFinalReport(c.id)}>
                                                                Commissioner asked
                                                            </button>
                                                        ) : <span className="chip mute">not requested</span>}
                                                    </td>
                                                    <td className="mono">{c.commission_reference ?? "—"}</td>
                                                    {isStaff && <td>{c.company_name ?? "TrustCare"}</td>}
                                                </tr>
                                            );
                                        })}
                                        {(wall.data?.clocks ?? []).length === 0 && (
                                            <tr><td colSpan={isStaff ? 7 : 6} className="empty">
                                                No Commission clocks running. Starting one on a reportable incident computes the
                                                24-hour and 5-business-day deadlines and alerts managers before they run out.
                                            </td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </>
                        )}

                        {tab === "extended" && (
                            <>
                                <div style={{ padding: "0.5rem 0.8rem" }}>
                                    <select aria-label="Filter by register" value={extRegister}
                                        onChange={(e) => setExtRegister(e.target.value)}>
                                        <option value="">All registers</option>
                                        {EXTENDED_REGISTERS.map(([value, label]) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                                <table>
                                    <thead><tr>
                                        <th>Reference</th><th>Register</th><th>Entry</th><th>Rating</th>
                                        <th>Due</th><th>Owner</th><th>Status</th>{isStaff && <th>Organisation</th>}
                                    </tr></thead>
                                    <tbody>
                                        {(entries.data ?? []).map((e) => (
                                            <tr key={e.id}>
                                                <td>
                                                    <div className="mono" style={{ fontWeight: 600 }}>{e.ref_code ?? "—"}</div>
                                                    {e.case_ref && (
                                                        <div className="chip pass mono" style={{ marginTop: 3, fontWeight: 700 }}>{e.case_ref}</div>
                                                    )}
                                                </td>
                                                <td>{registerLabel(e.register)}</td>
                                                <td style={{ maxWidth: 300 }}>
                                                    <div style={{ fontWeight: 600 }}>{e.title}</div>
                                                    {e.description && (
                                                        <div style={{ overflow: "hidden", display: "-webkit-box",
                                                            WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                                                            fontSize: "0.85em" }}>
                                                            {e.description}
                                                        </div>
                                                    )}
                                                </td>
                                                <td><span className={`chip ${severityChip(e.rating)}`}>
                                                    {e.rating ? titleCase(e.rating) : "—"}</span></td>
                                                <td className="mono">{e.due_date ? formatDate(e.due_date) : "—"}</td>
                                                <td>{e.owner_name ?? "—"}</td>
                                                <td>
                                                    <span className={`chip ${statusChip(e.status)}`}>{titleCase(e.status.replace(/_/g, " "))}</span>
                                                    {mayCloseEntry && e.status !== "closed" && (
                                                        <button className="icon-btn" style={{ marginLeft: 5 }}
                                                            onClick={() => void closeEntry(e.id)}>
                                                            Close
                                                        </button>
                                                    )}
                                                </td>
                                                {isStaff && <td>{e.company_name ?? "TrustCare"}</td>}
                                            </tr>
                                        ))}
                                        {(entries.data ?? []).length === 0 && (
                                            <tr><td colSpan={isStaff ? 8 : 7} className="empty">
                                                Nothing recorded {extRegister ? "in this register" : "yet"}. Risk entries take TC-RSK,
                                                corrective actions TC-CAP, audits TC-AUD — each register has its own reference.
                                            </td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </>
                        )}

                        {tab === "feedback" && (
                            <table>
                                <thead><tr>
                                    <th>Reference</th><th>Type</th><th>From</th><th>About</th>
                                    <th>Received</th><th>Status</th>{isStaff && <th>Organisation</th>}
                                </tr></thead>
                                <tbody>
                                    {(feedback.data ?? []).map((f) => (
                                        <tr key={f.id}>
                                            <td className="mono" style={{ fontWeight: 600 }}>{f.ref_code ?? "—"}</td>
                                            <td>
                                                <span className={`chip ${typeChip(f.submission_type)}`}>{titleCase(f.submission_type)}</span>
                                                {f.risk_rating && (
                                                    <span className={`chip ${severityChip(f.risk_rating)}`} style={{ marginLeft: 4 }}>
                                                        {titleCase(f.risk_rating)}
                                                    </span>
                                                )}
                                            </td>
                                            <td>{f.anonymous ? <span className="chip mute">Anonymous</span> : f.submitter_name ?? "—"}</td>
                                            <td style={{ maxWidth: 320 }}>
                                                <div style={{ overflow: "hidden", display: "-webkit-box",
                                                    WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                                                    {f.description ?? "—"}
                                                </div>
                                            </td>
                                            <td className="mono">{formatDate(f.submitted_at)}</td>
                                            <td>
                                                <span className={`chip ${statusChip(f.status)}`}>{titleCase(f.status)}</span>
                                                {/* Q4: when it moved matters to the person who raised it. */}
                                                {(f.resolved_at || f.acknowledged_at) && (
                                                    <div className="mono" style={{ marginTop: 3, fontSize: "0.82em" }}>
                                                        {f.resolved_at
                                                            ? `Resolved ${formatDate(f.resolved_at)}`
                                                            : `Acknowledged ${formatDate(f.acknowledged_at!)}`}
                                                    </div>
                                                )}
                                            </td>
                                            {isStaff && <td>{f.company_name ?? "TrustCare"}</td>}
                                        </tr>
                                    ))}
                                    {(feedback.data ?? []).length === 0 && (
                                        <tr><td colSpan={isStaff ? 7 : 6} className="empty">
                                            Nothing recorded yet. Complaints take a TC-CML reference, compliments TC-CMP and feedback TC-FBK.
                                        </td></tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>}
                </div>
            </div>
        </div>
    );
};
